import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../api/client";
import {
  MANEUVER_ITEMS,
  RATING_CRITERIA,
  type ManeuverCode,
  type RatingCriterionCode,
  averageLessonRating,
} from "../../constants/lessonReportCatalog";
import {
  RichLessonReport,
  fetchReportByAppointment,
  submitLessonReport,
} from "../../api/lessonReports";
import { UI_LOADING } from "../../constants/uiLabels";
import { LessonReportManeuverIcon } from "./lessonReportManeuverIcons";
import LessonReportStarDisplay from "./LessonReportStarDisplay";
import { ManeuverStepper } from "./ManeuverStepper";
import { lessonReportsPathForStudent } from "../../utils/lessonReportsNav";
import "./LessonReportModal.css";

type Props = {
  appointmentId: string;
  studentId?: string;
  studentName?: string;
  instructorName?: string;
  vehiclePlate?: string;
  startsAt?: string;
  endsAt?: string;
  onClose: () => void;
  onSaved?: (report: RichLessonReport | null, kmAutoUpdated?: boolean, kmDelta?: number) => void;
};

const HIGH_KM_DELTA = 30;

function parseCounts(raw: Record<string, unknown> | undefined): Partial<Record<ManeuverCode, number>> {
  if (!raw) return {};
  const out: Partial<Record<ManeuverCode, number>> = {};
  for (const item of MANEUVER_ITEMS) {
    const v = raw[item.code];
    if (typeof v === "number" && v > 0) out[item.code] = v;
  }
  return out;
}

function parseStars(raw: Record<string, unknown> | undefined): Partial<Record<RatingCriterionCode, number>> {
  if (!raw) return {};
  const out: Partial<Record<RatingCriterionCode, number>> = {};
  for (const item of RATING_CRITERIA) {
    const v = raw[item.code];
    if (typeof v === "number" && v >= 1 && v <= 5) out[item.code] = v;
  }
  return out;
}

export default function LessonReportModal({
  appointmentId,
  studentId,
  studentName,
  instructorName,
  vehiclePlate,
  startsAt,
  endsAt,
  onClose,
  onSaved,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [attended, setAttended] = useState(true);
  const [startKm, setStartKm] = useState("");
  const [endKm, setEndKm] = useState("");
  const [maneuverCounts, setManeuverCounts] = useState<Partial<Record<ManeuverCode, number>>>({});
  const [ratingScores, setRatingScores] = useState<Partial<Record<RatingCriterionCode, number>>>({});
  const [examReady, setExamReady] = useState(false);
  const [generalNotes, setGeneralNotes] = useState("");
  const [noShowReason, setNoShowReason] = useState("");
  const [existingReport, setExistingReport] = useState<RichLessonReport | null>(null);

  const lessonAvg = useMemo(() => averageLessonRating(ratingScores), [ratingScores]);

  const reportsListStudentId = studentId ?? existingReport?.student_id ?? null;

  const kmDelta = useMemo(() => {
    if (!attended || !startKm.trim() || !endKm.trim()) return null;
    const sk = Number(startKm);
    const ek = Number(endKm);
    if (!Number.isFinite(sk) || !Number.isFinite(ek) || ek < sk) return null;
    return ek - sk;
  }, [attended, startKm, endKm]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchReportByAppointment(appointmentId)
      .then((r) => {
        if (cancelled) return;
        setExistingReport(r);
        if (r) {
          setAttended(r.attended);
          setStartKm(r.start_km !== null ? String(r.start_km) : "");
          setEndKm(r.end_km !== null ? String(r.end_km) : "");
          setManeuverCounts(parseCounts(r.maneuver_counts as Record<string, unknown>));
          setRatingScores(parseStars(r.rating_scores as Record<string, unknown>));
          setExamReady(Boolean(r.exam_ready));
          setGeneralNotes(r.general_notes || "");
          setNoShowReason(r.no_show_reason || "");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Mevcut rapor yüklenemedi.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  const setManeuverCountValue = (code: ManeuverCode, n: number) => {
    const clamped = Math.max(0, Math.min(99, n));
    setManeuverCounts((prev) => {
      const next = { ...prev };
      if (clamped <= 0) delete next[code];
      else next[code] = clamped;
      return next;
    });
  };

  const setRating = (code: RatingCriterionCode, stars: number) => {
    setRatingScores((prev) => {
      const next = { ...prev };
      if (prev[code] === stars) delete next[code];
      else next[code] = stars;
      return next;
    });
  };

  const buildPayload = (confirmHighKmDelta: boolean) => {
    const maneuverPayload: Partial<Record<ManeuverCode, number>> = {};
    for (const [k, v] of Object.entries(maneuverCounts)) {
      if (typeof v === "number" && v > 0) maneuverPayload[k as ManeuverCode] = v;
    }
    const parseKm = (raw: string) => {
      const n = Number(raw);
      if (!Number.isFinite(n)) return null;
      return Math.round(n);
    };
    return {
      appointmentId,
      attended,
      startKm: attended ? parseKm(startKm) : null,
      endKm: attended ? parseKm(endKm) : null,
      maneuverCounts: maneuverPayload,
      ratingScores,
      examReady: attended ? examReady : false,
      generalNotes: generalNotes.trim() || null,
      nextLessonSuggestion: null,
      noShowReason: !attended ? noShowReason.trim() : null,
      confirmHighKmDelta,
    };
  };

  const handleSubmit = async () => {
    setError(null);
    setStatusMessage(null);
    if (!attended && !noShowReason.trim()) {
      setError("Gelmedi seçildiğinde sebep Zorunludur.");
      return;
    }
    if (attended) {
      if (!startKm.trim() || !endKm.trim()) {
        setError("Katıldı ise başlangıç ve bitiş KM Zorunludur.");
        return;
      }
      const sk = Math.round(Number(startKm));
      const ek = Math.round(Number(endKm));
      if (!Number.isFinite(sk) || !Number.isFinite(ek) || sk < 0 || ek < 0) {
        setError("KM değerleri geçerli tam sayı olmalıdır.");
        return;
      }
      if (ek < sk) {
        setError("Bitiş KM, başlangıç KM'den küçük olamaz.");
        return;
      }
    }

    let confirmHighKmDelta = false;
    if (attended && kmDelta !== null && kmDelta > HIGH_KM_DELTA) {
      const ok = window.confirm(
        `Bu derste ${kmDelta} km farkı var (eşik: ${HIGH_KM_DELTA} km). Yine de kaydetmek istiyor musunuz?`,
      );
      if (!ok) return;
      confirmHighKmDelta = true;
    }

    setBusy(true);
    try {
      const response = await submitLessonReport(buildPayload(confirmHighKmDelta));
      const km = response.km_auto_updated ? ` Araç KM ${response.km_delta} km ileri alındı.` : "";
      setStatusMessage(`Rapor kaydedildi.${km}`);
      const fresh = await fetchReportByAppointment(appointmentId);
      setExistingReport(fresh);
      onSaved?.(fresh, response.km_auto_updated, response.km_delta);
    } catch (err) {
      if (err instanceof ApiError && err.code === "HIGH_KM_DELTA_CONFIRM_REQUIRED") {
        const retry = window.confirm(`${err.message}\n\nOnaylayıp tekrar denemek ister misiniz?`);
        if (retry) {
          setBusy(false);
          await handleSubmitWithConfirm();
          return;
        }
      }
      setError(err instanceof ApiError ? err.message : "Rapor kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitWithConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await submitLessonReport(buildPayload(true));
      const km = response.km_auto_updated ? ` Araç KM ${response.km_delta} km ileri alındı.` : "";
      setStatusMessage(`Rapor kaydedildi.${km}`);
      const fresh = await fetchReportByAppointment(appointmentId);
      setExistingReport(fresh);
      onSaved?.(fresh, response.km_auto_updated, response.km_delta);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Rapor kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="report-modal-backdrop" role="dialog" aria-modal="true">
      <div className="report-modal report-modal--v2">
        <div className="report-modal__header">
          <div>
            <h3>Ders Değerlendirmesi</h3>
            <div className="report-modal__sub">
              {studentName && <span>{studentName}</span>}
              {startsAt && endsAt && (
                <span>
                  {studentName ? " · " : ""}
                  {new Date(startsAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                  {" – "}
                  {new Date(endsAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {startsAt && !endsAt && (
                <span>
                  {studentName ? " · " : ""}
                  {new Date(startsAt).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                </span>
              )}
              {instructorName && <span> · {instructorName}</span>}
              {vehiclePlate && <span className="mono"> · {vehiclePlate}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="report-modal__close" aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="report-modal__body">
          {loading && <div className="report-modal__loading">{UI_LOADING}</div>}
          {!loading && (
            <>
              {existingReport && (
                <div className="report-modal__hint">
                  Bu randevunun raporu mevcut ({new Date(existingReport.updated_at).toLocaleString("tr-TR")}).
                </div>
              )}

              <div className="report-modal__attendance-card">
                <div className="report-modal__attendance-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
                    <rect x="5" y="3" width="14" height="18" rx="2" stroke="#64748b" strokeWidth="1.8" />
                    <path d="M9 8h6M9 12h6M9 16h4" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="report-modal__attendance-text">
                  <strong>Katılım Durumu</strong>
                  <span>Öğrenci derse katıldı mı?</span>
                </div>
                <div className="report-modal__attendance-actions" role="radiogroup" aria-label="Katılım durumu">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={attended}
                    className={`report-modal__attendance-pill${attended ? " is-on is-on--yes" : ""}`}
                    onClick={() => setAttended(true)}
                  >
                    Katıldı ✓
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!attended}
                    className={`report-modal__attendance-pill${!attended ? " is-on is-on--no" : ""}`}
                    onClick={() => setAttended(false)}
                  >
                    Gelmedi
                  </button>
                </div>
                {reportsListStudentId && (
                  <Link
                    to={lessonReportsPathForStudent(reportsListStudentId)}
                    className="report-modal__detail-reports-link"
                    onClick={onClose}
                  >
                    Ders Detay Raporu
                  </Link>
                )}
              </div>

              {attended ? (
                <>
                  <div className="report-modal__row">
                    <label>
                      Başlangıç KM <span className="report-modal__req">*</span>
                      <input
                        type="number"
                        className="kp-input"
                        value={startKm}
                        onChange={(e) => setStartKm(e.target.value)}
                        min={0}
                        required
                      />
                    </label>
                    <label>
                      Bitiş KM <span className="report-modal__req">*</span>
                      <input
                        type="number"
                        className="kp-input"
                        value={endKm}
                        onChange={(e) => setEndKm(e.target.value)}
                        min={0}
                        required
                      />
                    </label>
                    <div className="report-modal__delta">
                      {kmDelta !== null && (
                        <span
                          className={`badge ${kmDelta > HIGH_KM_DELTA ? "badge-warn" : "badge-ok"}`}
                        >
                          +{kmDelta} km
                        </span>
                      )}
                    </div>
                  </div>

                  <section className="report-modal__section report-modal__section--maneuvers">
                    <h4 className="report-modal__section-title">Manevra Tekrarları</h4>
                    <p className="report-modal__section-hint">
                      Her alıştırma için tekrar sayısını artı/eksi ile girin.
                    </p>
                    <ul className="report-modal__maneuver-list">
                      {MANEUVER_ITEMS.map((m) => (
                        <li key={m.code} className="report-modal__maneuver-row">
                          <LessonReportManeuverIcon code={m.code} />
                          <div className="report-modal__maneuver-main">
                            <span className="report-modal__maneuver-label">{m.label}</span>
                            <ManeuverStepper
                              label={m.label}
                              value={maneuverCounts[m.code] ?? 0}
                              onChange={(n) => setManeuverCountValue(m.code, n)}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section className="report-modal__section report-modal__section--ratings">
                    <h4 className="report-modal__section-title">Değerlendirme (1–5 Yıldız)</h4>
                    <p className="report-modal__section-hint">
                      İsteğe bağlı — doldurulan maddelerin ortalaması ders puanıdır.
                    </p>
                    <div className="report-modal__rating-list">
                      {RATING_CRITERIA.map((c) => (
                        <div key={c.code} className="report-modal__rating-row">
                          <span className="report-modal__rating-label">{c.label}</span>
                          <div className="report-modal__star-row">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <button
                                key={s}
                                type="button"
                                className={`report-modal__star ${ratingScores[c.code] !== undefined && s <= (ratingScores[c.code] ?? 0) ? "is-on" : ""}`}
                                onClick={() => setRating(c.code, s)}
                                aria-label={`${s} yıldız`}
                              >
                                ★
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {lessonAvg !== null && (
                      <div
                        className="report-modal__lesson-avg-panel"
                        aria-label={`Ders ortalaması ${lessonAvg} / 5`}
                      >
                        <span className="report-modal__lesson-avg-caption-label">Ders Ortalaması</span>
                        <span className="report-modal__lesson-avg-caption-value">
                          {Math.round(lessonAvg * 10) / 10}
                        </span>
                        <LessonReportStarDisplay
                          value={lessonAvg}
                          showValue={false}
                          className="report-modal__lesson-avg-stars"
                        />
                      </div>
                    )}
                  </section>

                  <label className="report-modal__exam-ready">
                    <input
                      type="checkbox"
                      checked={examReady}
                      onChange={(e) => setExamReady(e.target.checked)}
                    />
                    Sınava Hazır
                  </label>

                  <label>
                    <span className="report-modal__label">Ders Notu</span>
                    <textarea
                      className="kp-input"
                      rows={3}
                      maxLength={2000}
                      value={generalNotes}
                      onChange={(e) => setGeneralNotes(e.target.value)}
                    />
                  </label>
                </>
              ) : (
                <label>
                  <span className="report-modal__label">Gelmeme Sebebi (Zorunlu)</span>
                  <textarea
                    className="kp-input"
                    rows={3}
                    maxLength={500}
                    value={noShowReason}
                    onChange={(e) => setNoShowReason(e.target.value)}
                    placeholder="Örn: Haber vermeden gelmedi"
                  />
                </label>
              )}

              {error && <div className="report-modal__error">{error}</div>}
              {statusMessage && <div className="report-modal__success">{statusMessage}</div>}
            </>
          )}
        </div>

        <div className="report-modal__footer report-modal__footer--v2">
          <button type="button" className="report-modal__btn-cancel" onClick={onClose} disabled={busy}>
            İptal
          </button>
          <button
            type="button"
            className="report-modal__btn-save"
            onClick={handleSubmit}
            disabled={busy || loading}
          >
            <span className="report-modal__btn-save-icon" aria-hidden>
              <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor">
                <path d="M4 3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.414a2 2 0 0 0-.586-1.414l-3.414-3.414A2 2 0 0 0 12.586 2H4zm8 1.414L16.586 9H14a2 2 0 0 1-2-2V4.414zM6 13h8v2H6v-2z" />
              </svg>
            </span>
            {busy ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}
